import React, { ComponentProps, ElementType, useCallback, useEffect, useRef, useState } from 'react'
import { Captcha } from '../components'
import { uuid } from '../utils'
import { useUnmount } from 'ahooks'
import useScript from 'react-script-hook'
import { pollingUntil } from '../utils/polling'
import { file } from '../services/api/bff/task/api'
import { getDeviceToken } from '@common/oneclub/utils'
import { useCaptchaLanguageChange } from './useCaptchaLanguageChange'
import ReactDOM from 'react-dom'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from '../store'
import { setCaptchaStatus } from '../store/captchaSlice'
import { OneModal } from '@yy/one-ui'

/**
 * 验证码
 * @returns
 */
export const useCaptcha = () => {
  const captchaLang = useCaptchaLanguageChange()
  const { t } = useTranslation()

  const renderCaptcha = useCallback(
    ({ onCaptchaSuccess, onCaptchaCancel, captchaType, bizParam, captchaScene }) => {
      const ele = document.createElement('div')
      ele.id = uuid('captcha-modal-container')
      document.body.appendChild(ele)
      const destory = () => {
        if (ele.parentNode) {
          ele.parentNode.removeChild(ele)
        }
      }

      return new Promise((resolve, reject) => {
        ReactDOM.render(
          <Captcha
            id="captcha-account-type-choose"
            // className={styles.captcha}
            visible={true}
            lang={captchaLang}
            onSuccess={(token: string, captchaType: string) => {
              onCaptchaSuccess && onCaptchaSuccess(token, captchaType)
              resolve({
                token,
                captchaType,
              })
              destory()
            }}
            onCancel={() => {
              onCaptchaCancel && onCaptchaCancel()
              reject('cancel')
              destory()
            }}
            captchaType={captchaType}
            bizParam={bizParam}
            captchaScene={captchaScene}
            appCode="m3tdgo"
          />,
          ele
        )
      }) as Promise<{ token: string; captchaType: string }>
    },
    [captchaLang]
  )

  const validateDownloadUrl = useCallback(
    async ({ taskId, token, url, captchaScene }: { taskId: string; token: string; url: string; captchaScene: string }) => {
      try {
        if (token && url && !url.startsWith('https')) {
          // 请求震龙接口拿到真正的url // 但是也有可能是需要校验的
          const dfpToken = await getDeviceToken()
          const { url: fileUrl, riskResult } = await file(
            {
              taskId: taskId,
            },
            {
              headers: {
                mToken: token,
                dfpToken,
              },
            }
          )
          // 如果url存在，完成正常逻辑
          if (Boolean(fileUrl)) {
            return { url: fileUrl }
          } else {
            // 如果是拦截的话，就不需要走下面的流程了。直接reject
            if (riskResult?.hitPunishType === 'intercept') {
              OneModal.error({
                title: t('risk.intercept.title'),
                content: t('risk.intercept.content'),
                cancelText: t('common.Ikonw'),
              })

              return Promise.reject('intercept')
            }
            // 根据riskresult信息进行动态校验
            // 注入验证码
            const { token: mToken } = await renderCaptcha({
              captchaType: riskResult.hitPunishType,
              bizParam: {
                serialNo: riskResult.serialNo,
              },
              captchaScene,
            })

            const { url: realUrl } = await file(
              {
                taskId: taskId,
              },
              {
                headers: {
                  mToken: mToken,
                  dfpToken,
                  serialNo: riskResult.serialNo,
                  verifyType: riskResult.hitPunishType == 'authn' ? 'identity' : 'captcha',
                },
              }
            )
            return { url: realUrl }
          }
        }
        return { url }
      } catch (error) {
        return Promise.reject(error)
      }
    },
    [renderCaptcha, t]
  )
  return {
    validateDownloadUrl,
  }
}

{
  /* <CaptchaDownloadButton onClick=(_, token ) => {
  console.log(token)
}}></CaptchaDownloadButton> */
}

const useMmcToken = (id: string, publicKey: string) => {
  const [loading] = useScript({
    src: `https://r2cdn.myshopline.com/static/rs/acuf/prod/latest/bundle.iife.js`,
    checkForExisting: true,
  })
  const init = useRef(false)
  const mmcInstance = useRef<any>(null)
  const tokenRef = useRef('')
  const listener = useRef<any>(null)
  const getMToken = useCallback(async () => {
    if (tokenRef.current) return Promise.resolve(tokenRef.current)
    const { promise } = pollingUntil(
      () => {
        return Promise.resolve(tokenRef.current)
      },
      (data) => {
        return Boolean(data)
      }
    )
    await promise
    return Promise.resolve(tokenRef.current)
  }, [])
  const createListener = useCallback(() => {
    mmcInstance.current = window.DeviceFingerprint?.mmc({
      env: 'same_domain',
      publicKey,
      timeout: 500,
      debounceTime: 800,
    })

    if (document.getElementById(id)) {
      listener.current = mmcInstance.current.listen(id, (token: string) => {
        tokenRef.current = token
      })
    }
  }, [id, publicKey])
  useEffect(() => {
    if (loading || init.current) return
    createListener()
    init.current = true
  }, [createListener, loading])

  useUnmount(() => {
    if (listener.current) {
      listener.current?.destroy()
    }
  })
  return {
    getMToken,
  }
}

type WithCaptchaProps<T extends ElementType> = {
  publicKey: string
  children?: React.ReactNode
  onCaptchaClick?: (event: Parameters<Extract<ComponentProps<T>, 'onClick'>>[0], token: string) => Promise<void>
}

export const wrapCaptcha = <T extends ElementType>(Component: T): React.FC<Omit<ComponentProps<T>, 'onClick'> & WithCaptchaProps<T>> => {
  return (props) => {
    const { publicKey, onCaptchaClick, children, ...rest } = props
    const id = uuid('captcha')
    const { getMToken } = useMmcToken(id, publicKey)
    const [loading, setLoading] = useState(false)
    const inCaptchaing = useSelector((state) => state.captchaSlice.inCaptchaing)
    const dispatch = useDispatch()
    const inCaptchaingRef = useRef<boolean>(false)
    useEffect(() => {
      inCaptchaingRef.current = inCaptchaing
    }, [inCaptchaing])

    const handleClick = useCallback(
      async (event: Parameters<Extract<ComponentProps<T>, 'onClick'>>[0]) => {
        ;(event as any).stopPropagation()
        if (inCaptchaingRef.current) {
          return
        }
        try {
          setLoading(true)
          await dispatch(setCaptchaStatus(true))
          inCaptchaingRef.current = true
          const originalOnClick = rest.onClick as Extract<ComponentProps<T>, 'onClick'>
          if (originalOnClick) {
            originalOnClick(event)
          }

          // 执行验证码回调（传入组件原生事件 + token）
          if (onCaptchaClick) {
            const token = await getMToken()
            await onCaptchaClick(event, token)
          }
        } catch (error) {
        } finally {
          if (inCaptchaingRef.current) {
            await dispatch(setCaptchaStatus(false))
            inCaptchaingRef.current = false
            setLoading(false)
          }
        }
      },
      [dispatch, getMToken, onCaptchaClick, rest.onClick]
    )

    return React.createElement(Component, {
      ...rest,
      id,
      onClick: handleClick,
      loading,
      children,
    })
  }
}
